import type { ApolloQueryResult, Operation } from '@apollo/client';
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { useChangeRequestFileUpdate } from '@risksmart-app/components/src/file/useFileUpdate';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { getEnv } from '@risksmart-app/components/src/utils/environment';
import { isRecoverableRefreshTokenError } from '@risksmart-app/components/src/utils/errorUtils';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import { GetActionByIdDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createClient } from 'graphql-ws';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ConfirmChangeRequestModal } from 'src/components/change-requests-preview/ConfirmChangeRequestModal';
import type { useGetActionById } from 'src/hooks/queries/action/useGetActionById';

import { evictField } from '@/utils/graphqlUtils';

import apolloMetricLink from './apolloMetricLink';
import { createGraphqlErrorHandler } from './graphqlErrorHandler';

type GetActionByIdData = NonNullable<
  ReturnType<typeof useGetActionById>['data']
>;

export default function ApolloGraphqlProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { addNotification } = useNotifications();
  const { getAccessTokenSilently, getIdTokenClaims } = useRisksmartUser();
  const [
    confirmChangeRequestModalVisible,
    setConfirmChangeRequestModalVisible,
  ] = useState(false);
  const [approvalOperation, setApprovalOperation] = useState<null | Operation>(
    null
  );

  const errorLink = useMemo(
    () =>
      // Log any GraphQL errors or network error that occurred
      //https://www.apollographql.com/docs/react/api/link/apollo-link-error
      // Handler extracted to `graphqlErrorHandler.ts` so its branch selection is
      // unit-testable without React (RSP-6971).
      onError(
        createGraphqlErrorHandler((operation) => {
          setConfirmChangeRequestModalVisible(true);
          setApprovalOperation(operation);
        })
      ),
    []
  );

  const getAuthHeaders = useCallback(async () => {
    let accessToken = null;
    let claims = null;
    const headers: Record<string, string> = {};

    try {
      accessToken = await getAccessTokenSilently();
      claims = await getIdTokenClaims();
    } catch (error) {
      // Missing/invalid refresh tokens are expected session-lifecycle events —
      // swallow them so the finally block redirects to /logout instead of the
      // error crashing the page through the error boundary (RSP-4478).
      if (!isRecoverableRefreshTokenError(error)) {
        throw error;
      }
    } finally {
      // Logout if access token is not available
      if (!accessToken) {
        window.location.href = '/logout';
      }
    }

    if (accessToken) {
      headers.authorization = `Bearer ${accessToken}`;
    }
    if (claims?.claims_tenant) {
      const tenant = claims.claims_tenant;
      headers['x-tenant-name'] = tenant.toLowerCase();
    }

    return { headers };
  }, [getAccessTokenSilently, getIdTokenClaims]);

  const client = useMemo(() => {
    const authLink = setContext(async (_, { headers }) => {
      const { headers: newHeaders } = await getAuthHeaders();
      const combinedHeaders = {
        ...headers,
        ...newHeaders,
      };

      return { headers: combinedHeaders };
    });

    const customUrlFetch = async (
      uri: RequestInfo | URL,
      options: RequestInit | undefined
    ) => {
      uri = getEnv('REACT_APP_TENANT_API_URL') + '/v1/graphql';

      if (options) {
        options.mode = 'cors';
      }

      return fetch(uri, options);
    };

    const httpLink = new HttpLink({
      fetch: customUrlFetch,
    });

    const wsLink = new GraphQLWsLink(
      createClient({
        url: async () => {
          const environment = getEnv('REACT_APP_ENVIRONMENT', true);
          if (environment === 'dev-local' || environment === 'ephemeral') {
            return getEnv('REACT_APP_WS_TENANT_API_URL');
          }
          const { headers } = await getAuthHeaders();
          const tenantName = headers['x-tenant-name'];
          const hostName = getEnv('REACT_APP_WS_TENANT_API_URL');

          return `${hostName}/${tenantName}/v1/graphql`;
        },
        connectionParams: async () => {
          const { headers } = await getAuthHeaders();

          return { headers };
        },
      })
    );

    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);

        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        );
      },
      wsLink,
      httpLink
    );

    return new ApolloClient({
      // link: authLink.concat(httpLink),
      // set to true to debug caching issues in dev
      devtools: { enabled: false },
      link: ApolloLink.from([apolloMetricLink, errorLink, authLink, splitLink]),
      cache: new InMemoryCache(),
    });
  }, [errorLink, getAuthHeaders]);

  const { updateFiles } = useChangeRequestFileUpdate();

  return (
    <ApolloProvider client={client}>
      {confirmChangeRequestModalVisible && (
        <ConfirmChangeRequestModal
          onDismiss={() => setConfirmChangeRequestModalVisible(false)}
          onConfirm={(requesterComment) => {
            if (!approvalOperation) {
              return;
            }
            toast.dismiss();
            addNotification({
              type: 'promise',
              // TODO: translation
              successMessage: 'Change request confirmed',
              // TODO: translation
              errorMessage: 'Failed to confirm change request',
              promise: (async () => {
                const result = await client.mutate({
                  mutation: approvalOperation.query,
                  variables: approvalOperation.variables,
                  context: {
                    ...approvalOperation.getContext(),
                    headers: {
                      ...approvalOperation.getContext()?.headers,
                      'x-confirm-change-request': 'true',
                      ...(requesterComment
                        ? {
                            'x-requester-comment':
                              encodeURIComponent(requesterComment),
                          }
                        : {}),
                    },
                  },
                });

                const parentId =
                  result?.data?.updateChildAction?.change_request_id;

                if (
                  parentId &&
                  (approvalOperation.operationName === 'updateAction' ||
                    approvalOperation.operationName === 'closeAction')
                ) {
                  const action: ApolloQueryResult<GetActionByIdData> =
                    await client.query({
                      query: GetActionByIdDocument,
                      variables: { _eq: approvalOperation.variables.Id },
                      fetchPolicy: 'no-cache',
                    });

                  const originalFiles = action?.data?.action[0]?.files || [];

                  await updateFiles({
                    parentType: Parent_Type_Enum.ChangeRequest,
                    parentId: parentId,
                    originalFiles: originalFiles
                      .map((originalFile) => originalFile.file)
                      .filter((originalFile) => !!originalFile),
                    selectedFiles: approvalOperation?.variables?.files,
                  });
                }

                evictField(client.cache, 'change_request');

                return client.refetchQueries({ include: ['all'] });
              })(),
            });
          }}
        />
      )}
      {children}
    </ApolloProvider>
  );
}
